import { createZip, type ZipEntry } from '@/utils/zip'
import { saveBlob } from '@/utils/download'
import type { Asset } from '@/services/assets/assetsApi'

/**
 * The file an asset downloads as. A zip-uploaded asset (an HTML creative) keeps
 * its original archive rather than the single file the CDN serves from it, and
 * an image falls back to its preview rendition when the original is absent.
 */
function downloadSource(asset: Asset): { url: string; name: string } | null {
  const url = asset.zipCdnId || asset.cdnId || asset.previewCdnId
  if (!url) return null
  return { url, name: asset.zipName || asset.fileName || asset.id }
}

/** The CDN is a different origin, so the bytes are fetched rather than linked. */
async function fetchAsset(url: string, name: string): Promise<Response> {
  let res: Response
  try {
    res = await fetch(url)
  } catch (e) {
    // A cross-origin block surfaces here as an opaque TypeError, so name the file
    throw new Error(`${name} could not be fetched from the CDN (${(e as Error).message})`)
  }
  if (!res.ok) throw new Error(`${name} returned ${res.status} from the CDN`)
  return res
}

/**
 * Saves one asset under its own file name. If the bytes cannot be read — a CDN
 * that refuses cross-origin reads, say — the browser is handed the URL instead,
 * which at worst opens the file in a new tab rather than failing outright.
 */
export async function downloadAsset(asset: Asset): Promise<void> {
  const source = downloadSource(asset)
  if (!source) throw new Error('This asset has no file to download')

  try {
    const res = await fetchAsset(source.url, source.name)
    saveBlob(await res.blob(), source.name)
  } catch {
    const a = document.createElement('a')
    a.href = source.url
    a.download = source.name
    a.target = '_blank'
    a.rel = 'noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

/** Bundles every downloadable asset into one archive. Returns how many it packed. */
export async function downloadAssetsZip(assets: Asset[], fileBase: string): Promise<number> {
  const sources = assets.map(downloadSource).filter((s): s is { url: string; name: string } => !!s)
  if (sources.length === 0) throw new Error('None of these assets have a file to download')

  const used = new Set<string>()
  const entries: ZipEntry[] = await Promise.all(
    sources.map(async ({ url, name }, index) => {
      // Two assets can share a file name; the archive cannot
      let entryName = name
      if (used.has(entryName)) {
        const dot = name.lastIndexOf('.')
        entryName =
          dot > 0 ? `${name.slice(0, dot)}-${index + 1}${name.slice(dot)}` : `${name}-${index + 1}`
      }
      used.add(entryName)
      const res = await fetchAsset(url, name)
      return { name: entryName, content: new Uint8Array(await res.arrayBuffer()) }
    })
  )

  saveBlob(createZip(entries), `${fileBase}.zip`)
  return entries.length
}
